<template>
  <div v-loading="loading" class="nx-page nx-report-page kanban-temperature-humidity-chart">
    <el-form :inline="true" class="th-filter-bar" size="small" @submit.native.prevent>
      <el-form-item label="时间">
        <el-date-picker
          v-model="timeRange"
          type="datetimerange"
          value-format="yyyy-MM-dd HH:mm:ss"
          range-separator="至"
          start-placeholder="开始时间"
          end-placeholder="结束时间"
          :default-time="['00:00:00', '23:59:59']"
          :picker-options="pickerOptions"
          class="th-time-picker"
        />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" icon="el-icon-search" @click="loadData">查询</el-button>
        <el-button icon="el-icon-refresh-left" @click="resetFilters">重置</el-button>
      </el-form-item>
    </el-form>

    <div class="th-kpi-row">
      <div class="th-kpi blue">
        <div class="th-kpi-label">有数据配电室</div>
        <div class="th-kpi-value">{{ rooms.length }}</div>
        <div class="th-kpi-sub">按当前筛选时间统计</div>
      </div>
      <div class="th-kpi green">
        <div class="th-kpi-label">温度点位</div>
        <div class="th-kpi-value">{{ totalTemperaturePointCount }}</div>
        <div class="th-kpi-sub">当前结果统计</div>
      </div>
      <div class="th-kpi teal">
        <div class="th-kpi-label">湿度点位</div>
        <div class="th-kpi-value">{{ totalHumidityPointCount }}</div>
        <div class="th-kpi-sub">当前结果统计</div>
      </div>
      <div class="th-kpi amber">
        <div class="th-kpi-label">最后采集时间</div>
        <div class="th-kpi-value time">{{ latestCollectTimeText }}</div>
        <div class="th-kpi-sub">接口返回最新值</div>
      </div>
    </div>

    <el-empty v-if="!rooms.length && !loading" :description="emptyDescription" />

    <template v-else>
      <div class="th-room-tabs">
        <button
          v-for="room in rooms"
          :key="room.roomCode || room.roomId || room.roomName"
          type="button"
          :class="['th-room-tab', { active: activeRoomCode === roomKey(room) }]"
          @click="setActiveRoom(room)"
        >
          <span>{{ room.roomName || room.roomCode || '未命名配电室' }}</span>
          <em>温度 {{ pointCount(room.temperaturePoints) }} 个 / 湿度 {{ pointCount(room.humidityPoints) }} 个</em>
        </button>
      </div>

      <section class="th-chart-card">
        <header class="th-chart-head">
          <strong>{{ currentRoomName }}温湿度总览趋势</strong>
          <small>{{ queryRangeText }}</small>
        </header>
        <div v-if="hasOverviewData" ref="overviewChart" class="th-chart-box" />
        <el-empty v-else description="暂无温湿度总览数据" />
      </section>

      <section class="th-chart-card">
        <header class="th-chart-head">
          <strong>温度分点位趋势</strong>
          <small>{{ queryRangeText }}</small>
        </header>
        <div v-if="hasTemperaturePointData" ref="temperatureChart" class="th-chart-box" />
        <el-empty v-else description="暂无温度点位数据" />
      </section>

      <section class="th-chart-card">
        <header class="th-chart-head">
          <strong>湿度分点位趋势</strong>
          <small>{{ queryRangeText }}</small>
        </header>
        <div v-if="hasHumidityPointData" ref="humidityChart" class="th-chart-box" />
        <el-empty v-else description="暂无湿度点位数据" />
      </section>
    </template>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { fetchTemperatureHumidityChart } from './api'

const TEMPERATURE_COLOR = '#2f80ed'
const HUMIDITY_COLOR = '#16a085'
const POINT_COLORS = ['#2f80ed', '#16a085', '#f59e0b', '#8b5cf6', '#ef4444', '#14b8a6', '#64748b', '#ec4899']

function pad(value) {
  return String(value).padStart(2, '0')
}

function formatDateTime(date) {
  return [
    date.getFullYear(),
    pad(date.getMonth() + 1),
    pad(date.getDate())
  ].join('-') + ' ' + [
    pad(date.getHours()),
    pad(date.getMinutes()),
    pad(date.getSeconds())
  ].join(':')
}

function todayRange() {
  const now = new Date()
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0)
  return [formatDateTime(start), formatDateTime(now)]
}

export default {
  name: 'KanbanTemperatureHumidityDynamicChart',
  data() {
    return {
      loading: false,
      timeRange: todayRange(),
      result: {
        generatedTime: '',
        rooms: []
      },
      hasQueried: false,
      activeRoomCode: '',
      chartInstances: [],
      pickerOptions: {
        shortcuts: [
          {
            text: '今天',
            onClick(picker) {
              const now = new Date()
              const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0)
              picker.$emit('pick', [start, now])
            }
          },
          {
            text: '最近24小时',
            onClick(picker) {
              const end = new Date()
              const start = new Date(end.getTime() - 24 * 60 * 60 * 1000)
              picker.$emit('pick', [start, end])
            }
          },
          {
            text: '最近7天',
            onClick(picker) {
              const end = new Date()
              const start = new Date(end.getTime() - 7 * 24 * 60 * 60 * 1000)
              picker.$emit('pick', [start, end])
            }
          }
        ]
      }
    }
  },
  computed: {
    rooms() {
      return (this.result && this.result.rooms) || []
    },
    currentRoom() {
      if (!this.rooms.length) return {}
      return this.rooms.find(room => this.roomKey(room) === this.activeRoomCode) || this.rooms[0]
    },
    currentRoomName() {
      return this.currentRoom.roomName || this.currentRoom.roomCode || ''
    },
    labels() {
      return this.currentRoom.labels || this.currentRoom.xAxis || []
    },
    totalTemperaturePointCount() {
      return this.rooms.reduce((sum, room) => sum + this.pointCount(room.temperaturePoints), 0)
    },
    totalHumidityPointCount() {
      return this.rooms.reduce((sum, room) => sum + this.pointCount(room.humidityPoints), 0)
    },
    latestCollectTimeText() {
      const times = []
      this.rooms.forEach(room => {
        if (room.lastCollectTime) times.push(room.lastCollectTime)
      })
      const latest = times.sort().pop() || (this.result && this.result.generatedTime)
      return latest ? String(latest).slice(11, 19) || latest : '--'
    },
    emptyDescription() {
      return this.hasQueried ? '暂无温湿度历史数据' : '请选择时间后点击查询'
    },
    queryRangeText() {
      const start = (this.result && this.result.startTime) || (this.normalizedTimeRange()[0] || '')
      const end = (this.result && this.result.endTime) || (this.normalizedTimeRange()[1] || '')
      return start && end ? start + ' 至 ' + end : ''
    },
    hasOverviewData() {
      return this.hasSeriesData(this.currentRoom.temperatureSeries) || this.hasSeriesData(this.currentRoom.humiditySeries)
    },
    hasTemperaturePointData() {
      return this.hasPointData(this.currentRoom.temperaturePoints)
    },
    hasHumidityPointData() {
      return this.hasPointData(this.currentRoom.humidityPoints)
    }
  },
  watch: {
    activeRoomCode() {
      this.$nextTick(() => this.renderCharts())
    }
  },
  mounted() {
    window.addEventListener('resize', this.onResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
    this.disposeCharts()
  },
  methods: {
    roomKey(room) {
      if (!room) return ''
      return String(room.roomCode || room.roomId || room.roomName || '')
    },
    pointCount(points) {
      return Array.isArray(points) ? points.length : 0
    },
    hasSeriesData(series) {
      return Array.isArray(series) && series.some(value => value !== null && value !== undefined && value !== '')
    },
    hasPointData(points) {
      return Array.isArray(points) && points.some(point => this.hasSeriesData(point.series))
    },
    setActiveRoom(room) {
      this.activeRoomCode = this.roomKey(room)
    },
    resetFilters() {
      this.timeRange = todayRange()
      this.loadData()
    },
    normalizeTimeValue(value) {
      if (value instanceof Date) return formatDateTime(value)
      return value
    },
    normalizedTimeRange() {
      if (!Array.isArray(this.timeRange) || this.timeRange.length !== 2) return []
      return [this.normalizeTimeValue(this.timeRange[0]), this.normalizeTimeValue(this.timeRange[1])]
    },
    validateTimeRange() {
      const range = this.normalizedTimeRange()
      if (range.length !== 2 || !range[0] || !range[1]) {
        this.$message.warning('请选择正确的时间范围')
        return false
      }
      const start = new Date(range[0].replace(/-/g, '/'))
      const end = new Date(range[1].replace(/-/g, '/'))
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end.getTime() <= start.getTime()) {
        this.$message.warning('请选择正确的时间范围')
        return false
      }
      if (end.getTime() - start.getTime() > 31 * 24 * 60 * 60 * 1000) {
        this.$message.warning('时间范围不能超过31天')
        return false
      }
      return true
    },
    async loadData() {
      if (!this.validateTimeRange()) return
      this.loading = true
      try {
        this.hasQueried = true
        const range = this.normalizedTimeRange()
        const raw = await fetchTemperatureHumidityChart({
          startTime: range[0],
          endTime: range[1]
        })
        this.result = {
          startTime: raw && raw.startTime,
          endTime: raw && raw.endTime,
          generatedTime: raw && raw.generatedTime,
          bucketGranularity: raw && raw.bucketGranularity,
          rooms: raw && raw.rooms ? raw.rooms : []
        }
        if (!this.rooms.some(room => this.roomKey(room) === this.activeRoomCode)) {
          this.activeRoomCode = this.rooms.length ? this.roomKey(this.rooms[0]) : ''
        }
        this.$nextTick(() => this.renderCharts())
      } catch (e) {
        this.hasQueried = true
        this.result = { generatedTime: '', rooms: [] }
        this.activeRoomCode = ''
        this.disposeCharts()
        this.$message.error((e && e.message) || '温湿度趋势数据加载失败')
      } finally {
        this.loading = false
      }
    },
    disposeCharts() {
      this.chartInstances.forEach(chart => chart && chart.dispose())
      this.chartInstances = []
    },
    onResize() {
      this.chartInstances.forEach(chart => chart && chart.resize())
    },
    renderCharts() {
      this.disposeCharts()
      if (!this.currentRoom || !this.labels.length) return
      if (this.hasOverviewData && this.$refs.overviewChart) {
        this.renderOverviewChart()
      }
      if (this.hasTemperaturePointData && this.$refs.temperatureChart) {
        this.renderPointChart(this.$refs.temperatureChart, this.currentRoom.temperaturePoints, '温度', this.currentRoom.temperatureUnit || '℃')
      }
      if (this.hasHumidityPointData && this.$refs.humidityChart) {
        this.renderPointChart(this.$refs.humidityChart, this.currentRoom.humidityPoints, '湿度', this.currentRoom.humidityUnit || '%')
      }
    },
    renderOverviewChart() {
      const chart = echarts.init(this.$refs.overviewChart)
      const temperatureUnit = this.currentRoom.temperatureUnit || '℃'
      const humidityUnit = this.currentRoom.humidityUnit || '%'
      chart.setOption({
        color: [TEMPERATURE_COLOR, HUMIDITY_COLOR],
        tooltip: {
          trigger: 'axis',
          formatter: params => this.axisTooltip(params)
        },
        legend: { data: ['温度', '湿度'], bottom: 0 },
        grid: { left: 54, right: 54, top: 28, bottom: 54 },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: this.labels
        },
        yAxis: [
          { type: 'value', name: temperatureUnit, scale: true },
          { type: 'value', name: humidityUnit, scale: true }
        ],
        series: [
          {
            name: '温度',
            type: 'line',
            smooth: true,
            connectNulls: false,
            data: this.currentRoom.temperatureSeries || [],
            yAxisIndex: 0,
            symbol: 'circle',
            symbolSize: 5,
            lineStyle: { width: 3 }
          },
          {
            name: '湿度',
            type: 'line',
            smooth: true,
            connectNulls: false,
            data: this.currentRoom.humiditySeries || [],
            yAxisIndex: 1,
            symbol: 'circle',
            symbolSize: 5,
            lineStyle: { width: 3 }
          }
        ]
      })
      this.chartInstances.push(chart)
    },
    renderPointChart(el, points, metricName, defaultUnit) {
      const validPoints = (points || []).filter(point => this.hasSeriesData(point.series))
      const chart = echarts.init(el)
      chart.setOption({
        color: POINT_COLORS,
        tooltip: {
          trigger: 'axis',
          formatter: params => this.axisTooltip(params)
        },
        legend: {
          type: 'scroll',
          bottom: 0,
          data: validPoints.map(point => point.pointName || point.pointCode || metricName)
        },
        grid: { left: 54, right: 24, top: 28, bottom: 58 },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: this.labels
        },
        yAxis: { type: 'value', name: defaultUnit, scale: true },
        series: validPoints.map((point, index) => ({
          name: point.pointName || point.pointCode || metricName + (index + 1),
          type: 'line',
          smooth: true,
          connectNulls: false,
          data: (point.series || []).map(value => ({ value, unit: point.unit || defaultUnit })),
          symbol: 'circle',
          symbolSize: 4,
          lineStyle: { width: 2 }
        }))
      })
      this.chartInstances.push(chart)
    },
    axisTooltip(params) {
      const list = Array.isArray(params) ? params : []
      if (!list.length) return ''
      const rows = [list[0].axisValueLabel || list[0].name || '']
      list.forEach(item => {
        const dataUnit = item.data && item.data.unit
        const unit = item.seriesType === 'line' && item.seriesName === '温度'
          ? this.currentRoom.temperatureUnit || '℃'
          : item.seriesType === 'line' && item.seriesName === '湿度'
            ? this.currentRoom.humidityUnit || '%'
            : dataUnit || ''
        const value = item.value === null || item.value === undefined || item.value === '' ? '--' : item.value
        rows.push(item.marker + item.seriesName + ': ' + value + (unit || ''))
      })
      return rows.join('<br/>')
    }
  }
}
</script>

<style lang="scss" scoped>
@import './index.scss';
</style>
